/**
 * Multi-language (i18n) configuration.
 *
 * To add a new language:
 * 1. Add the locale code to SUPPORTED_LOCALES below.
 * 2. Create src/locales/{code}.json with the same keys as en.json.
 * 3. Import the JSON and add it to the resources object below.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import tr from '../locales/tr.json';
import en from '../locales/en.json';

/** Supported locale codes. Add new languages here and create locales/{code}.json */
export const SUPPORTED_LOCALES = ['tr', 'en'] as const;
export type LocaleCode = (typeof SUPPORTED_LOCALES)[number];

/** Default when browser language is not supported */
export const DEFAULT_FALLBACK_LOCALE: LocaleCode = 'en';

const resources: Record<string, { translation: Record<string, string> }> = {
  tr: { translation: tr as Record<string, string> },
  en: { translation: en as Record<string, string> },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: DEFAULT_FALLBACK_LOCALE,
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'buildmate_lang',
      caches: ['localStorage'],
      convertDetectedLanguage: (lng: string) => {
        const base = lng.split('-')[0].toLowerCase();
        return SUPPORTED_LOCALES.includes(base as LocaleCode) ? base : DEFAULT_FALLBACK_LOCALE;
      },
    },
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
